import { WORKFLOW_VERTICAL_GAP_EXPANDED } from "@/lib/workflow/layoutPolicy";
import { type Edge, type Node } from "@xyflow/react";
import type { WorkflowEdgeData } from "../_components/WorkflowEdge";
import { buildWorkflowGraphLookups, collectAllLoopBodyDescendantIds } from "../_lib/workflowGraphAnalysis";
import { stratify, type HierarchyNode } from "d3-hierarchy";

// Gap between nodes
const NODE_GAP_VERTICAL = WORKFLOW_VERTICAL_GAP_EXPANDED; // Vertical spacing between levels
const NODE_GAP_HORIZONTAL = 120; // Horizontal spacing between branches
// Default node dimensions when measured dimensions aren't available
const DEFAULT_NODE_WIDTH = 280;
const DEFAULT_NODE_HEIGHT = 120;
// Extra vertical gap before onComplete section (no loop body children).
const ONCOMPLETE_GAP_VERTICAL = 60;
// Larger gap when loop body children are present — accounts for boundary bottom padding + "ON COMPLETE" label.
const ONCOMPLETE_GAP_VERTICAL_WITH_BODY = 100;

// Extended hierarchy node with layout data
interface LayoutNode extends HierarchyNode<Node> {
  subtreeWidth?: number;
  subtreeHeight?: number;
  x?: number;
  y?: number;
}

// Find connected components using BFS on the undirected graph formed by edges
function findConnectedComponents(nodes: Node[], edges: Edge[]): Node[][] {
  const nodeIds = new Set(nodes.map(n => n.id));
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  // Build adjacency list (undirected — both source→target and target→source)
  const adjacency = new Map<string, Set<string>>();
  for (const id of nodeIds) {
    adjacency.set(id, new Set());
  }
  for (const edge of edges) {
    if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
      adjacency.get(edge.source)!.add(edge.target);
      adjacency.get(edge.target)!.add(edge.source);
    }
  }

  const visited = new Set<string>();
  const components: Node[][] = [];

  for (const id of nodeIds) {
    if (visited.has(id)) continue;

    // BFS from this node
    const component: Node[] = [];
    const queue = [id];
    visited.add(id);

    while (queue.length > 0) {
      const current = queue.shift()!;
      component.push(nodeMap.get(current)!);

      for (const neighbor of adjacency.get(current)!) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }

    components.push(component);
  }

  // Sort: largest component first
  components.sort((a, b) => b.length - a.length);
  return components;
}

// Layout a single connected tree using d3-stratify
function layoutSingleTree(
  nodes: Node[],
  edges: Edge[],
  nodeDimensions: Map<string, { width: number; height: number }>,
  edgeSourceHandleMap: Map<string, string>,
  verticalGap: number,
): Node[] {
  // Guard: d3-stratify requires exactly one root per component
  const targetIds = new Set(edges.map(e => e.target));
  const roots = nodes.filter(n => !targetIds.has(n.id));
  if (roots.length !== 1) {
    console.warn(`layoutSingleTree: skipping component with ${roots.length} roots`);
    return nodes;
  }

  // Build set of onComplete target node IDs (direct targets of onComplete edges)
  const onCompleteTargetIds = new Set<string>();
  for (const e of edges) {
    if ((e.data as WorkflowEdgeData)?.isOnComplete) {
      onCompleteTargetIds.add(e.target);
    }
  }

  const parentMap = new Map(edges.map(e => [e.target, e.source]));
  const hierarchy = stratify<Node>()
    .id(d => d.id)
    .parentId((d: Node) => parentMap.get(d.id))(nodes) as LayoutNode;

  // Sort children: "else"/"timed_out" left, "then"/"resumed" right, onComplete children last
  function sortChildren(node: LayoutNode) {
    if (node.children) {
      node.children.sort((a, b) => {
        const aIsOC = onCompleteTargetIds.has(a.id!);
        const bIsOC = onCompleteTargetIds.has(b.id!);
        if (aIsOC && !bIsOC) return 1;
        if (!aIsOC && bIsOC) return -1;

        const handleA = edgeSourceHandleMap.get(a.id!);
        const handleB = edgeSourceHandleMap.get(b.id!);
        // Left-side branches: else, timed_out
        if (handleA === "source-else" || handleA === "source-timed_out") return -1;
        if (handleB === "source-else" || handleB === "source-timed_out") return 1;
        // Right-side branches: then, resumed
        if (handleA === "source-then" || handleA === "source-resumed") return 1;
        if (handleB === "source-then" || handleB === "source-resumed") return -1;
        return 0;
      });
      node.children.forEach(child => sortChildren(child as LayoutNode));
    }
  }
  sortChildren(hierarchy);

  // Helper: check if a hierarchy node is a for-each with onComplete children
  function isForEachWithOnComplete(node: LayoutNode): boolean {
    if ((node.data as Node).data?.type !== "for-each") return false;
    if (!node.children) return false;
    return node.children.some(c => onCompleteTargetIds.has(c.id!));
  }

  // Split children into loop body and onComplete groups
  function splitChildren(node: LayoutNode): {
    loopBody: LayoutNode[];
    onComplete: LayoutNode[];
  } {
    const loopBody: LayoutNode[] = [];
    const onComplete: LayoutNode[] = [];
    for (const child of node.children || []) {
      if (onCompleteTargetIds.has(child.id!)) {
        onComplete.push(child as LayoutNode);
      } else {
        loopBody.push(child as LayoutNode);
      }
    }
    return { loopBody, onComplete };
  }

  // Calculate subtree width (bottom-up).
  // For for-each nodes with onComplete: width = max(nodeWidth, loopBodyWidth, onCompleteWidth)
  // since loop body and onComplete are stacked vertically.
  function calculateSubtreeWidth(node: LayoutNode): number {
    const dims = nodeDimensions.get(node.id!) || { width: DEFAULT_NODE_WIDTH };
    const nodeWidth = dims.width;

    if (!node.children || node.children.length === 0) {
      node.subtreeWidth = nodeWidth;
      return nodeWidth;
    }

    if (isForEachWithOnComplete(node)) {
      const { loopBody, onComplete } = splitChildren(node);

      let loopBodyWidth = 0;
      loopBody.forEach((child, idx) => {
        loopBodyWidth += calculateSubtreeWidth(child);
        if (idx < loopBody.length - 1) loopBodyWidth += NODE_GAP_HORIZONTAL;
      });

      let onCompleteWidth = 0;
      onComplete.forEach((child, idx) => {
        onCompleteWidth += calculateSubtreeWidth(child);
        if (idx < onComplete.length - 1) onCompleteWidth += NODE_GAP_HORIZONTAL;
      });

      // Stacked vertically: width is the widest of all three
      node.subtreeWidth = Math.max(nodeWidth, loopBodyWidth, onCompleteWidth);
      return node.subtreeWidth;
    }

    // If-node with single child: allocate offset space so child is clearly on the correct side
    const isIfNodeForWidth = (node.data as Node).data?.type === "if";
    if (isIfNodeForWidth && node.children.length === 1) {
      const child = node.children[0] as LayoutNode;
      const childWidth = calculateSubtreeWidth(child);
      node.subtreeWidth = Math.max(nodeWidth, nodeWidth + NODE_GAP_HORIZONTAL + childWidth);
      return node.subtreeWidth;
    }

    // Default: children laid out horizontally
    let childrenTotalWidth = 0;
    node.children.forEach((child, idx) => {
      childrenTotalWidth += calculateSubtreeWidth(child as LayoutNode);
      if (idx < node.children!.length - 1) {
        childrenTotalWidth += NODE_GAP_HORIZONTAL;
      }
    });

    node.subtreeWidth = Math.max(nodeWidth, childrenTotalWidth);
    return node.subtreeWidth;
  }

  // Calculate subtree height (bottom-up).
  // For for-each with onComplete: height = node + loopBody + gap + onComplete (stacked)
  // For others: height = node + gap + max(children heights)
  function calculateSubtreeHeight(node: LayoutNode): number {
    const dims = nodeDimensions.get(node.id!) || { height: DEFAULT_NODE_HEIGHT };

    if (!node.children || node.children.length === 0) {
      node.subtreeHeight = dims.height;
      return dims.height;
    }

    if (isForEachWithOnComplete(node)) {
      const { loopBody, onComplete } = splitChildren(node);

      let maxLoopBodyHeight = 0;
      for (const child of loopBody) {
        maxLoopBodyHeight = Math.max(maxLoopBodyHeight, calculateSubtreeHeight(child));
      }

      let maxOnCompleteHeight = 0;
      for (const child of onComplete) {
        maxOnCompleteHeight = Math.max(maxOnCompleteHeight, calculateSubtreeHeight(child));
      }

      let totalHeight = dims.height;
      if (loopBody.length > 0) {
        totalHeight += verticalGap + maxLoopBodyHeight;
      }
      if (onComplete.length > 0) {
        const ocGap = loopBody.length > 0 ? ONCOMPLETE_GAP_VERTICAL_WITH_BODY : ONCOMPLETE_GAP_VERTICAL;
        totalHeight += ocGap + maxOnCompleteHeight;
      }

      node.subtreeHeight = totalHeight;
      return totalHeight;
    }

    // Default: children side by side, height = tallest child
    let maxChildHeight = 0;
    for (const child of node.children) {
      maxChildHeight = Math.max(maxChildHeight, calculateSubtreeHeight(child as LayoutNode));
    }
    node.subtreeHeight = dims.height + verticalGap + maxChildHeight;
    return node.subtreeHeight;
  }

  calculateSubtreeWidth(hierarchy);
  calculateSubtreeHeight(hierarchy);

  // Position nodes (top-down) — centered within subtree space, starting at x=0
  function positionNode(node: LayoutNode, x: number, y: number) {
    const dims = nodeDimensions.get(node.id!) || { width: DEFAULT_NODE_WIDTH, height: DEFAULT_NODE_HEIGHT };

    node.x = x + (node.subtreeWidth! - dims.width) / 2;
    node.y = y;

    if (!node.children || node.children.length === 0) return;

    if (isForEachWithOnComplete(node)) {
      const { loopBody, onComplete } = splitChildren(node);

      // Position loop body children horizontally below the for-each node
      if (loopBody.length > 0) {
        let loopBodyTotalWidth = 0;
        loopBody.forEach((child, idx) => {
          loopBodyTotalWidth += child.subtreeWidth!;
          if (idx < loopBody.length - 1) loopBodyTotalWidth += NODE_GAP_HORIZONTAL;
        });

        let childX = x + (node.subtreeWidth! - loopBodyTotalWidth) / 2;
        const childY = y + dims.height + verticalGap;

        for (const child of loopBody) {
          positionNode(child, childX, childY);
          childX += child.subtreeWidth! + NODE_GAP_HORIZONTAL;
        }
      }

      // Position onComplete children below the loop body subtree
      if (onComplete.length > 0) {
        let maxLoopBodySubtreeHeight = 0;
        for (const child of loopBody) {
          maxLoopBodySubtreeHeight = Math.max(maxLoopBodySubtreeHeight, child.subtreeHeight || 0);
        }

        let onCompleteY: number;
        if (loopBody.length > 0) {
          onCompleteY = y + dims.height + verticalGap + maxLoopBodySubtreeHeight + ONCOMPLETE_GAP_VERTICAL_WITH_BODY;
        } else {
          onCompleteY = y + dims.height + ONCOMPLETE_GAP_VERTICAL;
        }

        let onCompleteTotalWidth = 0;
        onComplete.forEach((child, idx) => {
          onCompleteTotalWidth += child.subtreeWidth!;
          if (idx < onComplete.length - 1) onCompleteTotalWidth += NODE_GAP_HORIZONTAL;
        });

        let childX = x + (node.subtreeWidth! - onCompleteTotalWidth) / 2;

        for (const child of onComplete) {
          positionNode(child, childX, onCompleteY);
          childX += child.subtreeWidth! + NODE_GAP_HORIZONTAL;
        }
      }

      return;
    }

    // Branching node with single child: position on the correct side
    // If-node: then=right, else=left. Wait-node: resumed=right, timed_out=left.
    const isBranchingNodeForPos = (node.data as Node).data?.type === "if" || (node.data as Node).data?.type === "wait";
    if (isBranchingNodeForPos && node.children.length === 1) {
      const child = node.children[0] as LayoutNode;
      const handle = edgeSourceHandleMap.get(child.id!);
      const childY = y + dims.height + verticalGap;

      if (handle === "source-then" || handle === "source-resumed") {
        positionNode(child, x + node.subtreeWidth! - child.subtreeWidth!, childY);
      } else if (handle === "source-else" || handle === "source-timed_out") {
        positionNode(child, x, childY);
      } else {
        positionNode(child, x + (node.subtreeWidth! - child.subtreeWidth!) / 2, childY);
      }
      return;
    }

    // Default: position all children horizontally
    let childX = x;
    const childY = y + dims.height + verticalGap;

    node.children.forEach(child => {
      const childNode = child as LayoutNode;
      positionNode(childNode, childX, childY);
      childX += childNode.subtreeWidth! + NODE_GAP_HORIZONTAL;
    });
  }
  positionNode(hierarchy, 0, 0);

  // Collect positioned nodes
  const result: Node[] = [];
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  function collectNodes(node: LayoutNode) {
    const originalNode = nodeMap.get(node.id!)!;
    result.push({
      ...originalNode,
      position: { x: node.x!, y: node.y! },
    });
    if (node.children) {
      node.children.forEach(child => collectNodes(child as LayoutNode));
    }
  }
  collectNodes(hierarchy);

  return result;
}

// Layouting function - accepts current nodes and edges and returns layouted nodes with updated positions
// Uses a proper tree layout that calculates subtree widths to prevent overlaps
// Handles multiple disconnected trees (e.g., when a user deletes a node in the middle)
export function layoutNodes(
  nodes: Node[],
  edges: Edge[],
  { verticalGap = NODE_GAP_VERTICAL }: { verticalGap?: number } = {},
): Node[] {
  if (nodes.length === 0) {
    return [];
  }

  // Filter out placeholder nodes from layout calculations
  const layoutableNodes = nodes.filter(n => !n.id.startsWith("placeholder-"));
  const placeholderNodes = nodes.filter(n => n.id.startsWith("placeholder-"));

  if (layoutableNodes.length === 0) {
    return nodes;
  }

  try {
    // Create shared dimension + handle maps
    const nodeDimensions = new Map<string, { width: number; height: number }>();
    layoutableNodes.forEach(node => {
      nodeDimensions.set(node.id, {
        width: node.measured?.width ?? DEFAULT_NODE_WIDTH,
        height: node.measured?.height ?? DEFAULT_NODE_HEIGHT,
      });
    });

    const edgeSourceHandleMap = new Map<string, string>();
    edges.forEach(e => {
      if (e.sourceHandle) {
        edgeSourceHandleMap.set(e.target, e.sourceHandle);
      }
    });

    // Build set of for-each loop body node IDs (+ their descendants) so we
    // can suppress placeholder nodes inside loop bodies — the boundary "+"
    // buttons handle adding nodes there.
    const { childrenMap, onCompleteTargetIds, forEachNodes } = buildWorkflowGraphLookups(layoutableNodes, edges);
    const loopBodyDescendantIds = collectAllLoopBodyDescendantIds(forEachNodes, childrenMap, onCompleteTargetIds, {
      excludePlaceholders: true,
    });

    // Find connected components (handles multiple disconnected trees)
    const components = findConnectedComponents(layoutableNodes, edges);

    // Layout each component separately, then place them side by side
    const positionedNodes: Node[] = [];
    let offsetX = 0;

    for (const component of components) {
      const componentNodeIds = new Set(component.map(n => n.id));
      const componentEdges = edges.filter(e => componentNodeIds.has(e.source) && componentNodeIds.has(e.target));

      const positioned = layoutSingleTree(component, componentEdges, nodeDimensions, edgeSourceHandleMap, verticalGap);

      // Calculate the bounding box of this tree
      let minX = Infinity;
      let maxX = -Infinity;
      for (const node of positioned) {
        const dims = nodeDimensions.get(node.id) || { width: DEFAULT_NODE_WIDTH };
        minX = Math.min(minX, node.position.x);
        maxX = Math.max(maxX, node.position.x + dims.width);
      }
      const treeWidth = maxX - minX;

      // Shift nodes so the tree starts at offsetX (subtract minX to normalize)
      for (const node of positioned) {
        node.position.x += offsetX - minX;
      }

      positionedNodes.push(...positioned);
      offsetX += treeWidth + NODE_GAP_HORIZONTAL * 2;
    }

    // Center all nodes around x=0
    if (positionedNodes.length > 0) {
      let totalMinX = Infinity;
      let totalMaxX = -Infinity;
      for (const node of positionedNodes) {
        const dims = nodeDimensions.get(node.id) || { width: DEFAULT_NODE_WIDTH };
        totalMinX = Math.min(totalMinX, node.position.x);
        totalMaxX = Math.max(totalMaxX, node.position.x + dims.width);
      }
      const centerOffset = (totalMinX + totalMaxX) / 2;
      for (const node of positionedNodes) {
        node.position.x -= centerOffset;
      }
    }

    // Position placeholder nodes relative to their parent.
    // Hide placeholders that are inside a for-each loop body (the boundary "+"
    // handles adding nodes there) or whose parent already has real children —
    // position them offscreen so they don't clutter the canvas.
    // For IF nodes: only hide a branch placeholder if THAT branch has a real child
    // (having a real child on the other branch shouldn't hide this placeholder).
    placeholderNodes.forEach(placeholder => {
      const incomingEdge = edges.find(e => e.target === placeholder.id);
      if (incomingEdge) {
        const parentId = incomingEdge.source;
        const placeholderSourceHandle = incomingEdge.sourceHandle;

        // For IF node branch placeholders (source-then/source-else), only check
        // if there's a real child on the SAME branch. Other branches' children
        // should not cause this placeholder to hide.
        // Narrow to IF nodes only — other node types with sourceHandle should
        // use the default "any real child" logic.
        const parentLayoutNode = layoutableNodes.find(n => n.id === parentId);
        const isIfNode = (parentLayoutNode?.data as Record<string, unknown>)?.type === "if";
        const usesBranchAwareCheck = isIfNode && !!placeholderSourceHandle;

        const parentHasRealChild = usesBranchAwareCheck
          ? edges.some(
              e =>
                e.source === parentId &&
                e.sourceHandle === placeholderSourceHandle &&
                e.target !== placeholder.id &&
                !e.target.startsWith("placeholder-"),
            )
          : edges.some(
              e => e.source === parentId && e.target !== placeholder.id && !e.target.startsWith("placeholder-"),
            );

        const shouldHide = loopBodyDescendantIds.has(parentId) || parentHasRealChild;

        if (shouldHide) {
          // Position offscreen — node still exists in the graph but is invisible
          positionedNodes.push({
            ...placeholder,
            position: { x: -9999, y: -9999 },
            hidden: true,
          });
          return;
        }

        const parentNode = positionedNodes.find(n => n.id === parentId);
        if (parentNode) {
          const parentDims = nodeDimensions.get(parentNode.id) || {
            width: DEFAULT_NODE_WIDTH,
            height: DEFAULT_NODE_HEIGHT,
          };
          positionedNodes.push({
            ...placeholder,
            position: {
              x: parentNode.position.x,
              y: parentNode.position.y + parentDims.height + verticalGap,
            },
          });
        } else {
          positionedNodes.push(placeholder);
        }
      } else {
        positionedNodes.push(placeholder);
      }
    });

    return positionedNodes;
  } catch (error) {
    console.error("Layout error:", error);
    return nodes;
  }
}
